import { Subject } from 'rxjs';
import { Toro } from '../common/enumerations';
import { ToroAccountDetailed } from '../models/toro-account-detailed.model';
import { User } from '../models/user.model';

export class LocalCacheManager {
    // Subjects
    doNotShowWhatsNewChange = new Subject<boolean>();

    private keys = {
        ApiEnvironment: 'ApiEnvironment',
        AccessToken: 'AccessToken',
        ContainerState: 'ContainerState',
        CurrentRoute: 'CurrentRoute',
        CurrentUser: 'CurrentUser',
        DetailedAccounts: 'DetailedAccounts',
        DoNotShowWhatsNew: 'DoNotShowWhatsNew',
        EmailAddress: 'EmailAddress',
        FwUpdateCancelled: 'FwUpdateCancelled',
        IsNavbarNarrow: 'IsNavbarNarrow',
        LastControllerImageIndex: 'LastControllerImageIndex',
        LastNotificationIds: 'LastNotificationIds',
        ManualRuntime: 'ManualRuntime',
        RememberMe: 'RememberMe',
        SelectedAccountId: 'SelectedAccountId',
        SelectedController: 'SelectedController',
        SelectedLocationId: 'SelectedLocationId',
        Theme: 'Theme',
        UserId: 'UserId',
        authState: "authState",
        LastMessageIdIfResultOfUserRequest: 'LastMessageIdIfResultOfUserRequest'
    };

    constructor() {
    }

    // Access Token
    get accessToken(): string {
        const cachedToken: string = localStorage.getItem(this.keys.AccessToken);
        return cachedToken ? cachedToken : '';
    }

    set accessToken(token: string) {
        localStorage.setItem(this.keys.AccessToken, token);
    }

    // User Id
    get userId(): number {
        let userId = +localStorage.getItem(this.keys.UserId);
        return userId ? userId : 0;
    }

    set userId(userId: number) {
        localStorage.setItem(this.keys.UserId, userId.toString());
    }

    // Current User
    get currentUser(): User {
        let currentUser = localStorage.getItem(this.keys.CurrentUser);
        return currentUser ? new User(JSON.parse(currentUser)) : null;
    }

    set currentUser(user: User) {
        localStorage.setItem(this.keys.CurrentUser, JSON.stringify(user));
    }

    // Detailed Accounts
    get detailedAccounts(): ToroAccountDetailed[] {

        let detailedAccounts = localStorage.getItem(this.keys.DetailedAccounts);
        const cachedDetailedAccounts = detailedAccounts ? JSON.parse(detailedAccounts) : null;

        if (cachedDetailedAccounts) {
            const detailedAccounts = [];

            cachedDetailedAccounts.forEach(acc => {
                detailedAccounts.push(new ToroAccountDetailed(acc));
            });

            return detailedAccounts;
        }

        return null;
    }

    set detailedAccounts(detailedAccounts: ToroAccountDetailed[]) {
        localStorage.setItem(this.keys.DetailedAccounts, JSON.stringify(detailedAccounts));
    }

    // Email Address
    get emailAddress(): string {
        return localStorage.getItem(this.keys.EmailAddress);
    }

    set emailAddress(emailAddress: string) {
        localStorage.setItem(this.keys.EmailAddress, emailAddress);
    }

    // RememberMe Flag
    get rememberMe(): boolean {
        return localStorage.getItem(this.keys.RememberMe) === 'true';
    }

    set rememberMe(value: boolean) {
        localStorage.setItem(this.keys.RememberMe, value ? 'true' : 'false');
    }

    get LastControllerImageIndex(): number {
        return +localStorage.getItem(this.keys.LastControllerImageIndex);
    }

    set LastControllerImageIndex(value: number) {
        if (value) {
            localStorage.setItem(this.keys.LastControllerImageIndex, value.toString());
        }
    }

    get selectedAccountId(): number {
        return +localStorage.getItem(this.keys.SelectedAccountId);
    }

    set selectedAccountId(value: number) {
        if (value) {
            localStorage.setItem(this.keys.SelectedAccountId, value.toString());
        }
    }

    get selectedLocationId(): number {
        return +localStorage.getItem(this.keys.SelectedLocationId);
    }

    set selectedLocationId(value: number) {
        if (value) {
            localStorage.setItem(this.keys.SelectedLocationId, value.toString());
        }
    }

    get FwUpdateCancelled(): boolean {
        return localStorage.getItem(this.keys.FwUpdateCancelled) === 'true';
    }

    set FwUpdateCancelled(value: boolean) {
        localStorage.setItem(this.keys.FwUpdateCancelled, String(value));
    }

    _lastNotificationIds: { [accountId: number]: string; } = {};
    _lastMessageIdIfResultOfUserRequest: { [accountId: number]: string; } = {};

    removeFwUpdateCancelled() {
        localStorage.removeItem(this.keys.FwUpdateCancelled);
    }

    saveLastNotificationId(accountId: number, lastNotificationId: string) {
        if (accountId && accountId > 0) {
            this._lastNotificationIds[accountId] = lastNotificationId;
            localStorage.setItem(this.keys.LastNotificationIds, JSON.stringify(this._lastNotificationIds));
        }
    }

    getLastNotificationId(accountId): string {
        if (accountId && accountId > 0) {
            if (this._lastNotificationIds) {
                return this._lastNotificationIds[accountId];
            }
            const IdsDict = localStorage.getItem(this.keys.LastNotificationIds);
            if (IdsDict) {
                this._lastNotificationIds = JSON.parse(localStorage.getItem(this.keys.LastNotificationIds));
                return this._lastNotificationIds[accountId];
            }
        } return null;
    }

    saveLastMessageIdIfResultOfUserRequest(accountId: number, lastMessageId: string) {
        if (accountId && accountId > 0) {
            this._lastMessageIdIfResultOfUserRequest[accountId] = lastMessageId;
            localStorage.setItem(this.keys.LastMessageIdIfResultOfUserRequest, JSON.stringify(this._lastMessageIdIfResultOfUserRequest));
        }
    }

    getLastMessageIdIfResultOfUserRequest(accountId): string {
        if (accountId && accountId > 0) {
            if (this._lastMessageIdIfResultOfUserRequest && this._lastMessageIdIfResultOfUserRequest[accountId]) {
                return this._lastMessageIdIfResultOfUserRequest[accountId];
            }

            const IdsDict = localStorage.getItem(this.keys.LastMessageIdIfResultOfUserRequest);
            if (IdsDict) {
                this._lastMessageIdIfResultOfUserRequest = JSON.parse(localStorage.getItem(this.keys.LastMessageIdIfResultOfUserRequest));
                return this._lastMessageIdIfResultOfUserRequest[accountId];
            }
        }
        return null;
    }

    // Tab Accordions State
    // containerContentsId represents a unique identifier for the container contents. For example, if the container contains Controller Zones, the
    // containerContentsId might be the Id of the parent Controller.
    getContainerState(navTab: Toro.NavTab, containerContentsId: number): Toro.ContainerState {
        const state = localStorage.getItem(`${this.keys.ContainerState}:${navTab}:${containerContentsId}`);
        return state ? +state : Toro.ContainerState.Open;
    }

    setContainerState(navTab: Toro.NavTab, containerContentsId: number, accordionState: Toro.ContainerState) {
        localStorage.setItem(`${this.keys.ContainerState}:${navTab}:${containerContentsId}`, String(accordionState));
    }

    // =========================================================================================================================================================
    // Manual Runtime for station
    // =========================================================================================================================================================

    getManualStationRuntime(controllerId: number, stationNumber: number): number {
        return +localStorage.getItem(`${this.keys.ManualRuntime}:${controllerId}:${stationNumber}`);
    }

    setManualStationRuntime(controllerId: number, stationNumber: number, durationInSeconds: number) {
        localStorage.setItem(`${this.keys.ManualRuntime}:${controllerId}:${stationNumber}`, durationInSeconds.toString());
    }

    clearAllManualStationRuntimes(controllerId: number, stationCount: number) {
        for (let i = 1; i <= stationCount; i++) {
            localStorage.removeItem(`${this.keys.ManualRuntime}:${controllerId}:${i}`);
        }
    }

    // =========================================================================================================================================================
    // Manual Runtime for station
    // =========================================================================================================================================================

    // Navbar
    get isNavbarLockedFromExpanding() {
        return window.innerWidth <= 600;
    }

    get isNavbarNarrow() {
        return localStorage.getItem(this.keys.IsNavbarNarrow) === 'true';
    }

    set isNavbarNarrow(value: boolean) {
        localStorage.setItem(this.keys.IsNavbarNarrow, value ? 'true' : 'false');
    }

    // =========================================================================================================================================================
    // What's New
    // =========================================================================================================================================================

    get doNotShowWhatsNew(): boolean {

        let doNotShow = localStorage.getItem(this.keys.DoNotShowWhatsNew);
        let WhatsNewflag = (doNotShow == undefined || doNotShow == 'undefined') ? false : (doNotShow == 'true' ? true : false);

        return WhatsNewflag;
    }

    set doNotShowWhatsNew(value: boolean) {
        localStorage.setItem(this.keys.DoNotShowWhatsNew, value ? 'true' : 'false');
        this.doNotShowWhatsNewChange.next(value);
    }

    public setDetailedAccountsFromState(accounts: ToroAccountDetailed[]) {
        this.detailedAccounts = accounts;
        return true;
    }
}
